import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { SkillListItem, SkillStats } from '@core/models/skill';
import { InjectorKeys } from '@core/utils/injector.keys';
import { BackendService } from '@core/prototypes/backend.service';
import { UserService } from '@core/services/user/user.service';
import { SkillsService } from '@core/services/employee/skills.service';
import { addParamForRoute, ignorePromise } from '@ebcont/ebcont.galaxy.js';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-skill-details',
  templateUrl: './skill-details.component.html',
  styleUrls: ['./skill-details.component.css'],
  standalone: false
})
export class SkillDetailsComponent implements OnInit, OnChanges {
  @Input() skill?: SkillListItem;
  @Output() closeClicked: EventEmitter<void> = new EventEmitter<void>();

  public skillStats: SkillStats = {};
  public selectedStars?: number;
  public sorting = 'asc';

  constructor(
    @Inject(InjectorKeys.SKILL) private service: BackendService<SkillListItem>,
    private userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
    public cd: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['skill'] && changes['skill'].currentValue) {
      this.reloadList();
    }
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((queryParams) => {
      const { stars } = queryParams;
      if (stars) {
        this.starSelected(stars);
      }
      this.reloadList();
    });
  }

  public reloadList(): void {
    if (this.skill) {
      (this.service as SkillsService).getSkillCounts(this.skill.skill, this.skill.type).subscribe((result) => {
        this.skillStats = result;
        if (this.selectedStars === undefined && Object.keys(result).length > 0) {
          const highestRatedStar = Object.entries(result)
            .filter(([, count]) => count > 0)
            .sort(([starA], [starB]) => Number(starB) - Number(starA))
            [0]?.[0];

          if (highestRatedStar) {
            this.starSelected(highestRatedStar);
          }
        }
        this.cd.detectChanges();
      });
    }
  }

  onCloseButtonClicked() {
    this.closeClicked.emit();
  }

  public asKeyValuePairs(stats: SkillStats): { key: string; value: number }[] {
    const pairs = Object.entries(stats).map(([key, value]) => {
      return { key, value };
    });
    pairs.sort((a, b) => {
      return b.key.localeCompare(a.key);
    });
    return pairs;
  }

  protected readonly JSON = JSON;

  getTotalEmployees() {
    if (!this.skillStats) {
      return 0;
    }
    return this.asKeyValuePairs(this.skillStats)
      .map((pair) => {
        return pair.value;
      })
      .reduce((a, b) => {
        return a + b;
      }, 0);
  }

  starSelected(key: string) {
    this.selectedStars = parseInt(key);
    ignorePromise(addParamForRoute(this.router, this.route, { stars: key }));
  }

  protected readonly parseInt = parseInt;

  getStarContainerClass(key: string) {
    return '--star-container ' + (parseInt(key) === this.selectedStars ? '--selected' : '');
  }

  sortingChanged(sorting: string) {
    this.sorting = sorting;
  }
}
